'use client'
import HeaderCta from 'components/Header/HeaderCta'
import { Button } from '@achieve/ascend'
import { MediaImageStandard, Typography } from 'components/Contentful'
import { AchieveLink } from 'components/AchieveLink'
import { useEffect, useState } from 'react'
import useLayoutMenuCondensed from 'hooks/useLayoutMenuCondensed'
import { _get } from 'utils/shared'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { useGraduate } from 'providers/GraduateProvider'
import { MOBILE_APP_DOWNLOAD_FDR_ROUTE, WALL_OF_FAME_ROUTE } from 'constants/navigation'
import { getPageContentTypes } from 'lib/getPageContentType'
import { WEBSITE_NAMES } from '@/constants/websites'
import classNames from 'classnames'
import { useBanner } from 'providers/BannerProvider'
import { useVisibilityGroup } from 'hooks/useVisibilityGroup'
import { getFdrPersistentHeaderVariant } from 'components/Header/FDRPersistentHeader/fdrPersistentHeaderRoutes'

export const STICKY_FOOTER_BLACKLIST_FULLPATH = ['/tools/heloc-payment-calculator?v=a']

export const STICKY_FOOTER_BLACKLIST = [
  '/',
  '/achieve-pro',
  '/achieve-pro/why-partner-with-us',
  '/achieve-pro/about',
  '/achieve-pro/licenses',
  '/achieve-pro/contact-us',
  '/l/[slug]',
  '/lp/[slug]',
  '/a/[slug]',
  '/privacy/policies',
  '/privacy/terms',
  '/privacy/privacy-policy',
  '/privacy/freedom-debt-relief-california-privacy-disclosures',
  '/privacy/freedom-debt-relief-california-privacy-disclosures-es',
  '/privacy/changes-to-program/',
  '/privacy/es/cambios-en-el-programa/',
  '/about/partners',
  '/smarter-debt',
  '/campaigns/10k-start-fresh',
  MOBILE_APP_DOWNLOAD_FDR_ROUTE,
]

export default function FooterSticky({
  specificHeaderCtaDataFiltered,
  headerContent = {},
  content,
  dynamicStickyNav,
  phoneData,
}) {
  const { websiteField } = getPageContentTypes()
  const [menuCondensed] = useLayoutMenuCondensed()
  const { dynamicPathname, fullAsPath, asPath } = useAppRouter()
  const { graduate } = useGraduate()
  const { link = {} } = headerContent
  const tp_subtitle = dynamicStickyNav ? null : _get(content, 'card.subtitle')
  const tp_totalRating = _get(content, 'card.totalRating')
  const tp_totalReviews = _get(content, 'card.totalReviews')
  const tp_text = dynamicStickyNav
    ? dynamicStickyNav?.content?.fields?.cardSubtitleText
    : tp_totalReviews
    ? `${tp_totalRating}/5 from ${tp_totalReviews} reviews`
    : tp_subtitle
  const tp_image = dynamicStickyNav
    ? dynamicStickyNav?.content?.fields?.cardImage
    : _get(content, 'card.image')

  const callToAssociatedNumber = () => {
    if (phoneData?.phoneNumber) window.open(`tel:${phoneData.phoneNumber}`, '_self')
  }
  const subLinkText = dynamicStickyNav ? dynamicStickyNav?.linkSubtext : 'From Freedom Debt Relief'
  const linkText = dynamicStickyNav
    ? dynamicStickyNav?.link?.fields?.linkText?.content[0]?.content[0]?.value
    : link.fields?.linkText?.content[0]?.content[0]?.value
  const linkUrl = dynamicStickyNav
    ? dynamicStickyNav?.link?.fields?.linkHref
    : specificHeaderCtaDataFiltered ?? link.fields?.linkHref

  const [visible, setVisible] = useState(menuCondensed)
  const { isBannerVisible } = useBanner()
  const { anyVisible } = useVisibilityGroup({}, false)

  useEffect(() => {
    if (menuCondensed) {
      setVisible(true)
    } else {
      setTimeout(() => setVisible(false), 300)
    }
  }, [menuCondensed])
  // 1. don't display if the active route is in the blacklist
  // 2. don't display if the active route is the Wall of Fame graduate tokenized route
  // 3. don't display if the active full path is in the full path blacklist (for query params cases)
  // 4. don't display on FDR pages where the mobile persistent header replaces it
  if (
    [...STICKY_FOOTER_BLACKLIST, ...(graduate ? [WALL_OF_FAME_ROUTE] : [])].includes(
      dynamicPathname
    ) ||
    STICKY_FOOTER_BLACKLIST_FULLPATH.includes(fullAsPath) ||
    (websiteField === WEBSITE_NAMES.FDR && getFdrPersistentHeaderVariant(asPath))
  )
    return null

  const isGreenDesign = [WEBSITE_NAMES.FDR, WEBSITE_NAMES.BILLS].includes(websiteField)
  const isBillsDesign = [WEBSITE_NAMES.BILLS].includes(websiteField)

  return (
    visible &&
    isBannerVisible && (
      <div
        className={` lg:hidden bg-white text-white flex flex-col z-50 shadow-inner
      ${menuCondensed ? 'animate-slideDown' : 'animate-slideUp'} ${
          anyVisible ? 'hidden' : 'block'
        }`}
      >
        <div
          className={classNames('grid grid-cols-10 gap-3 p-4 pb-0', {
            'py-2 px-3 pb-2': isGreenDesign,
          })}
        >
          {phoneData?.phoneNumber && (
            <div className="col-span-3">
              <AchieveLink
                noLink
                track={{
                  list_name: 'CALL STICKY FOOTER',
                  click_id: phoneData?.phoneNumber,
                  click_text: `Call Us`,
                  click_url: phoneData?.phoneNumber,
                  click_type: 'Call Us Click',
                  event_action: 'button_click',
                  nav_link_section: 'Sticky Footer',
                  track_event: 'call-sticky-footer',
                }}
                data-testid="call-sticky-footer"
              >
                <Button
                  color="secondary"
                  variant={isGreenDesign ? 'outlined' : 'contained'}
                  className={classNames('w-full px-4 py-3', {
                    'h-[52px]': isGreenDesign,
                  })}
                  onClick={() => callToAssociatedNumber()}
                >
                  <Typography variant="displayXs" fontWeight="bold" content="Call Us" />
                </Button>
              </AchieveLink>
            </div>
          )}
          <div
            className={classNames('col-span-7 flex flex-col gap-4', {
              'col-span-10!': !phoneData?.phoneNumber,
            })}
          >
            <HeaderCta
              siteEnv={headerContent?.siteEnv}
              linkUrl={linkUrl}
              linkText={linkText}
              size="large"
              className="[&_button]:w-full [&_button]:h-[48px] [&_button]:text-[18px]"
              data-testid="header-get-started-button"
              textClassName="displaySm text-lg! font-bold!"
              event={{
                list_name: 'GET STARTED STICKY FOOTER',
                nav_link_section: 'Sticky Footer',
                track_event: 'get-started-sticky-footer',
              }}
              condensed={true}
              buttonClassName={classNames({
                'h-[52px]!': isGreenDesign,
              })}
            />
            {isBillsDesign && subLinkText && (
              <div className="flex flex-row gap-2 items-center justify-center">
                <Typography
                  variant="bodySm"
                  fontWeight="bold"
                  className="text-black"
                  content={subLinkText}
                  data-testid="bills-sticky-footer-text"
                />
              </div>
            )}
          </div>
        </div>
        {tp_image && (
          <div
            className={classNames(
              'flex w-full items-center justify-center px-4 py-2 text-center text-14 font-medium lg:text-16 gap-2',
              { 'bg-green-50 py-1!': isGreenDesign }
            )}
          >
            <div
              className={classNames({
                'w-[180px] h-[25px]': isGreenDesign,
                'w-[165px] h-[20px]': !isGreenDesign,
              })}
            >
              <MediaImageStandard content={tp_image} />
            </div>
            <Typography
              variant={isGreenDesign ? 'headerXs' : 'bodyXs'}
              className={classNames(
                'pt-1 text-black whitespace-nowrap text-ellipsis overflow-hidden',
                {
                  'pt-0!': isGreenDesign,
                }
              )}
              content={tp_text}
            />
          </div>
        )}
      </div>
    )
  )
}
